// File operation utilities

/**
 * Format file size to human readable format
 * @param {number|undefined|null} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (typeof bytes !== "number" || isNaN(bytes) || bytes < 0) return "-";
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Get file extension from filename
 * @param {string} filename - Name of the file
 * @returns {string} File extension
 */
export const getFileExtension = (filename) => {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
};

/**
 * Get file type based on extension
 * @param {string} filename - Name of the file
 * @returns {string} File type category
 */
export const getFileType = (filename) => {
  const extension = getFileExtension(filename).toLowerCase();

  const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];
  const documentTypes = ["pdf", "doc", "docx", "txt", "rtf", "odt"];
  const spreadsheetTypes = ["xls", "xlsx", "csv", "ods"];
  const presentationTypes = ["ppt", "pptx", "odp"];
  const videoTypes = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];
  const audioTypes = ["mp3", "wav", "flac", "aac", "ogg", "wma"];
  const archiveTypes = ["zip", "rar", "7z", "tar", "gz", "bz2"];
  const codeTypes = [
    "js",
    "jsx",
    "ts",
    "tsx",
    "html",
    "css",
    "scss",
    "json",
    "xml",
    "py",
    "java",
    "c",
    "cpp",
    "cs",
    "php",
    "rb",
    "go",
    "rs",
  ];

  if (imageTypes.includes(extension)) return "image";
  if (documentTypes.includes(extension)) return "document";
  if (spreadsheetTypes.includes(extension)) return "spreadsheet";
  if (presentationTypes.includes(extension)) return "presentation";
  if (videoTypes.includes(extension)) return "video";
  if (audioTypes.includes(extension)) return "audio";
  if (archiveTypes.includes(extension)) return "archive";
  if (codeTypes.includes(extension)) return "code";

  return "file";
};

/**
 * Get file icon based on type
 * @param {string} filename - Name of the file
 * @returns {string} Icon name or class
 */
export const getFileIcon = (filename) => {
  const fileType = getFileType(filename);

  const iconMap = {
    image: "image",
    document: "description",
    spreadsheet: "grid_on",
    presentation: "slideshow",
    video: "movie",
    audio: "audiotrack",
    archive: "archive",
    code: "code",
    folder: "folder",
    file: "insert_drive_file",
  };

  return iconMap[fileType] || iconMap.file;
};

/**
 * Format date to readable string
 * @param {Date|string|undefined|null} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return "-";

  const dateObj = new Date(date);
  if (isNaN(dateObj)) return "-";

  const now = new Date();

  // Normalize times to dates only (ignore time-of-day)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thatDay = new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate()
  );

  const diffTime = today - thatDay;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7 && diffDays > 1) {
    return `${diffDays} days ago`;
  } else {
    return dateObj.toLocaleDateString();
  }
};

/**
 * Check if file is an image
 * @param {string} filename - Name of the file
 * @returns {boolean} True if file is an image
 */
export const isImage = (filename) => {
  return getFileType(filename) === "image";
};

/**
 * Check if file can be previewed
 * @param {string} filename - Name of the file
 * @returns {boolean} True if file can be previewed
 */
export const canPreview = (filename) => {
  const previewableTypes = ["image", "document", "code"];
  return previewableTypes.includes(getFileType(filename));
};

/**
 * Generate breadcrumb path from folder structure
 * @param {Object} currentFolder - Current folder object
 * @param {Array} allFolders - Array of all folders
 * @returns {Array} Breadcrumb array
 */
export const generateBreadcrumbs = (currentFolder, allFolders = []) => {
  const breadcrumbs = [{ id: null, name: "Root", path: "/" }];

  if (!currentFolder) return breadcrumbs;

  // Build path from current folder to root
  let folder = currentFolder;
  const path = [];

  while (folder) {
    path.unshift(folder);
    folder = allFolders.find((f) => f.id === folder.parentId);
  }

  path.forEach((folder) => {
    breadcrumbs.push({
      id: folder.id,
      name: folder.name,
      path: folder.path || `/${folder.name}`,
    });
  });

  return breadcrumbs;
};

/**
 * Sort files and folders
 * @param {Array} items - Array of files and folders
 * @param {string} sortBy - Sort criteria (name, size, modified)
 * @param {string} sortOrder - Sort order (asc, desc)
 * @returns {Array} Sorted array
 */
export const sortItems = (items, sortBy = "name", sortOrder = "asc") => {
  return [...items].sort((a, b) => {
    // Always put folders first
    if (a.type === "folder" && b.type !== "folder") {
      return -1;
    }
    if (b.type === "folder" && a.type !== "folder") {
      return 1;
    }

    // If both are folders or both are files, sort by the specified criteria
    let aValue, bValue;

    switch (sortBy) {
      case "name":
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case "size":
        aValue = a.size || 0;
        bValue = b.size || 0;
        break;
      case "modified":
        aValue = new Date(a.modified);
        bValue = new Date(b.modified);
        break;
      default:
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
    }

    if (sortOrder === "desc") {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    } else {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    }
  });
};

/**
 * Filter items based on search text
 * @param {Array} items - Array of files and folders
 * @param {string} searchText - Search query
 * @returns {Array} Filtered array
 */
export const filterItems = (items, searchText) => {
  if (!searchText) return items;

  const query = searchText.toLowerCase();
  return items.filter(
    (item) =>
      item.name.toLowerCase().includes(query) ||
      (item.type && item.type.toLowerCase().includes(query))
  );
};
